import {
  createContext,
  useReducer,
  useContext,
  type ReactNode,
  useEffect,
} from 'react';

type InteractionType = 'thread' | 'comment' | 'reply';

export type ReportReasonType =
  | 'Offensive'
  | 'Illegal or fraudulent'
  | 'Sexual or violent/aggressive'
  | 'Racial commentary'
  | 'Drug-related references'
  | 'Spam'
  | 'Commercial in nature'
  | '';

interface OptionDialogState {
  id: string;
  showOptionDialog: boolean;
  reportReason: ReportReasonType;
  interaction: InteractionType;
  submitting: boolean;
  isAuthor: boolean;
}

type OptionDialogAction =
  | {
      type: 'SHOW_DIALOG';
    }
  | {
      type: 'SUBMITTING';
    }
  | {
      type: 'CLOSE_DIALOG';
    }
  | {
      type: 'REPORT_REASON';
      payload: {
        reportReason: ReportReasonType;
      };
    }
  | {
      type: 'IS_AUTHOR';
      payload: {
        isAuthor: boolean;
      };
    };

interface OptionDialogContextType {
  optionDialogState: OptionDialogState;
  optionDialogDispatch: React.Dispatch<OptionDialogAction>;
}

const OptionDialogContext = createContext<OptionDialogContextType | undefined>(
  undefined,
);

interface OptionDialogProviderProps {
  children: ReactNode;
}

const optionDialogReducer = (
  state: OptionDialogState,
  action: OptionDialogAction,
): OptionDialogState => {
  const { type } = action;
  switch (type) {
    case 'SHOW_DIALOG':
      return {
        ...state,
        showOptionDialog: true,
        submitting: false,
      };
    case 'CLOSE_DIALOG':
      return {
        ...state,
        showOptionDialog: false,
        submitting: false,
      };
    case 'SUBMITTING':
      return {
        ...state,
        submitting: true,
      };
    case 'REPORT_REASON':
      return {
        ...state,
        reportReason: action.payload.reportReason,
      };
    case 'IS_AUTHOR':
      return {
        ...state,
        isAuthor: action.payload.isAuthor,
      };
    default:
      throw new Error('groupCardReducer Unknown action type');
  }
};

interface OptionDialogProviderProps {
  children: ReactNode;
  value: { interaction: InteractionType; id: string; isAuthor: boolean };
}

export const OptionDialogProvider: React.FC<OptionDialogProviderProps> = ({
  children,
  value: { interaction, id, isAuthor },
}: OptionDialogProviderProps) => {
  const [optionDialogState, optionDialogDispatch] = useReducer(
    optionDialogReducer,
    {
      showOptionDialog: false,
      interaction,
      id,
      reportReason: '',
      submitting: false,
      isAuthor,
    },
  );

  useEffect(() => {
    optionDialogDispatch({ type: 'IS_AUTHOR', payload: { isAuthor } });
  }, [isAuthor]);

  return (
    <OptionDialogContext.Provider
      value={{
        optionDialogState,
        optionDialogDispatch,
      }}
    >
      {children}
    </OptionDialogContext.Provider>
  );
};

export const useOptionDialog = (): OptionDialogContextType => {
  const context = useContext(OptionDialogContext);
  if (!context) {
    throw new Error('useGroupCard must be used within a GroupCardProvider');
  }
  return context;
};

export default OptionDialogProvider;
